class BakeBundleForm extends HTMLElement {
  constructor() {
    super();
    this.blockId = this.dataset.blockId;
    this.button = this.querySelector('.bake-bundle__button');
    this.errorMessage = this.querySelector('.bake-bundle__error');
    
    this.init();
  }

  init() {
    if (this.button) {
      this.button.addEventListener('click', this.addToCart.bind(this));
    }
  }

  async addToCart() {
    if (this.button.disabled) return;

    this.button.disabled = true;
    this.errorMessage.hidden = true;
    
    // Show loading state
    const spinner = this.button.querySelector('.loading-spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }

    try {
      const bundleBlock = this.closest('.bake-bundle-block');
      const productInputs = bundleBlock.querySelectorAll('input[name^="bundle_product_"]');
      const items = [];

      productInputs.forEach((input, index) => {
        if (input.value && !isNaN(input.value)) {
          items.push({
            id: parseInt(input.value),
            quantity: 1
          });
        }
      });

      if (items.length === 0) {
        throw new Error('No products found in bundle');
      }

      // Use the actual Shopify store URL for development mode
      let cartUrl = `${window.location.origin}/cart/add.js`;
      
      // If in development mode, use the actual Shopify store URL
      if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
        cartUrl = 'https://42c697-3.myshopify.com/cart/add.js';
      }

      // Add all items to cart
      const response = await fetch(cartUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items
        })
      });

      // Read the response body once
      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = 'Failed to add items to cart';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorMessage;
        } catch (jsonError) {
          // If JSON parsing fails, use response text or default message
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        // If JSON parsing fails, the response might be empty or invalid
        data = { success: true }; // Assume success if response was ok
      }
      
      // Update cart UI
      this.updateCartUI();
      
      // Show success message
      this.showSuccessMessage();

    } catch (error) {
      this.showError(error.message);
    } finally {
      // Hide loading state
      if (spinner) {
        spinner.classList.add('hidden');
      }
      this.button.disabled = false;
    }
  }

  updateCartUI() {
    // Update cart count
    const cartCountElements = document.querySelectorAll('.cart-count-bubble, .cart-item-count');
    if (cartCountElements.length > 0) {
      // Use the actual Shopify store URL for development mode
      let cartUrl = `${window.location.origin}/cart.js`;
      
      // If in development mode, use the actual Shopify store URL
      if (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost') {
        cartUrl = 'https://42c697-3.myshopify.com/cart.js';
      }
      
      fetch(cartUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch cart data');
          }
          return response.json();
        })
        .then(cart => {
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
          });
        })
        .catch(error => {
          // Don't show error to user for cart count update failure
        });
    }

    // Update cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      try {
        cartDrawer.refresh();
      } catch (error) {
        // Cart drawer refresh failed
      }
    }

    // Trigger custom event for other cart listeners
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'bake-bundle-block' }
      }));
    } catch (error) {
      // Cart event dispatch failed
    }
  }

  showSuccessMessage() {
    // Try to open cart drawer first
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const cartDrawerButton = document.querySelector('[aria-controls="CartDrawer"]');
      if (cartDrawerButton) {
        cartDrawerButton.click();
      }
    } else {
      // Fallback: show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'bake-bundle__success alert alert--success';
      successMessage.textContent = 'Bundle added to cart successfully!';
      successMessage.style.position = 'fixed';
      successMessage.style.top = '20px';
      successMessage.style.right = '20px';
      successMessage.style.zIndex = '9999';
      successMessage.style.padding = '15px 20px';
      successMessage.style.borderRadius = '8px';
      successMessage.style.backgroundColor = '#4CAF50';
      successMessage.style.color = 'white';
      successMessage.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      successMessage.style.fontSize = '14px';
      successMessage.style.fontFamily = 'Jost, sans-serif';
      
      document.body.appendChild(successMessage);
      
      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 3000);
    }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
      
      // Scroll to error message
      this.errorMessage.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      
      // Hide error after 5 seconds
      setTimeout(() => {
        this.errorMessage.hidden = true;
      }, 5000);
    }
  }
}

// Register the custom element
customElements.define('bake-bundle-form', BakeBundleForm);

// Handle variant changes and price updates
document.addEventListener('DOMContentLoaded', function() {
  const bundleBlocks = document.querySelectorAll('.bake-bundle-block');
  
  bundleBlocks.forEach(block => {
    updateBundleTotalPrice(block);
  });
  
  function updateBundleTotalPrice(bundleBlock) {
    const productInputs = bundleBlock.querySelectorAll('input[name^="bundle_product_"]');
    let totalPrice = 0;
    let totalOriginalPrice = 0;
    
    productInputs.forEach(input => {
      const price = parseInt(input.dataset.price) || 0;
      const compareAtPrice = parseInt(input.dataset.compareAtPrice) || price;
      
      totalPrice += price;
      totalOriginalPrice += compareAtPrice;
    });
    
    const totalCurrentElement = bundleBlock.querySelector('.bake-bundle__total-current');
    const totalOriginalElement = bundleBlock.querySelector('.bake-bundle__total-original');
    
    if (totalCurrentElement) {
      totalCurrentElement.textContent = formatMoney(totalPrice);
    }
    
    if (totalOriginalElement) {
      if (totalOriginalPrice > totalPrice) {
        totalOriginalElement.textContent = formatMoney(totalOriginalPrice);
        totalOriginalElement.style.display = 'block';
      } else {
        totalOriginalElement.style.display = 'none';
      }
    }
  }
  
  function formatMoney(cents) {
    // Simple money formatting - you might want to use Shopify's formatMoney function
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
  
  // Listen for variant changes if product variant pickers are updated
  document.addEventListener('variant:change', function(event) {
    const variantId = event.detail.variantId;
    const productId = event.detail.productId;
    
    // Find and update the corresponding bundle block input
    bundleBlocks.forEach(block => {
      const productInput = block.querySelector(`input[data-variant-id="${variantId}"]`);
      if (productInput) {
        productInput.value = variantId;
        productInput.dataset.price = event.detail.price;
        productInput.dataset.compareAtPrice = event.detail.compareAtPrice || event.detail.price;
        
        updateBundleTotalPrice(block);
      }
    });
  });
});
